import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import { pickAutocompleteFields } from 'server/models/Address/fieldgroups';
import { SR_API_ACTION_ADDRESS_REGISTER_ADDRESS_STRING } from 'api/utilityActionTypes';

import {
  createInitialValues,
  createFormValidationFn,
  showFieldIf,
  hideFieldIf,
} from 'client/helpers/formikHelpers';

import requestUtilityApi from 'client/actions/requestUtilityApi';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';

import SrBox from 'client/components/SrBox';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_HEIGHT_MEDIUM,
} from 'client/components/SrButton/SrButton';

const getFormFields = ({ formValues, utilityPartnerId, utilityPartnerList } = {}) => {
  return [
    hideFieldIf(utilityPartnerId, {
      title: 'Utility Partner',
      id: 'utilityPartnerId',
      type: 'select',
      properties: {
        placeholder: '--',
        enableResetOption: true,
        choices: (utilityPartnerList || []).map(({ name, id }) => ({
          label: name,
          value: id,
        })),
      },
      validations: ['required'],
    }),
    showFieldIf(formValues?.utilityPartnerId, {
      id: 'addressString',
      title: 'Address',
      type: 'smarty_address_autocomplete',
      properties: {},
    }),
  ];
};

const SrUtilityProfitAdminRegisterAddressForm = ({
  dispatch,
  utilityPartnerId,
  utilityPartnerList,
  afterSubmit,
}) => {
  const [submitErrorMessage, setSubmitErrorMessage] = useState(null);

  const onSubmit = (formValues, formikBag) => {
    const { utilityPartnerId, addressString } = formValues;
    setSubmitErrorMessage(null);
    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_ADDRESS_REGISTER_ADDRESS_STRING,
      payload: {
        utilityPartnerId: parseInt(utilityPartnerId, 10),
        addressString,
      },
      onSuccess(address) {
        formikBag.resetForm();

        if (afterSubmit) {
          afterSubmit(address);
        }

        if (!utilityPartnerId) {
          global.alert('Address registered successfully.');
        }
      },
      onError(error) {
        setSubmitErrorMessage(error?.message);
      },
    });
  };

  const initialFormFields = getFormFields({
    utilityPartnerId,
  });

  return (
    <Formik
      initialValues={createInitialValues({
        formFields: initialFormFields,
        defaultValues: {
          utilityPartnerId,
        },
      })}
      enableReinitialize
      validate={createFormValidationFn(initialFormFields)}
      onSubmit={onSubmit}
      render={(formikBag) => {
        const formValues = formikBag.values;
        const autocompleteAddressFields = pickAutocompleteFields(formValues);
        const addressFieldsValid = Object.keys(autocompleteAddressFields)
          .filter((fieldId) => fieldId !== 'county')
          .every((fieldId) => autocompleteAddressFields[fieldId]);

        const formFields = getFormFields({
          formValues,
          utilityPartnerId,
          utilityPartnerList,
        });

        return (
          <Form>
            {formFields.map((formField) => (
              <SrFormikField
                key={formField.id}
                formField={formField}
                formikBag={formikBag}
                theme={SR_FIELD_THEME_ADOBE}
              />
            ))}
            <SrBox padding="28px 0 12px">
              <SrButton
                theme={SR_BUTTON_THEME_ADOBE_GREEN}
                disabled={!formikBag.isValid || !formikBag.dirty || !addressFieldsValid}
                heightType={SR_BUTTON_HEIGHT_MEDIUM}
                hasBoxShadow
                type="submit"
              >
                {utilityPartnerId ? 'Submit' : 'Register Address'}
              </SrButton>
            </SrBox>
            {submitErrorMessage && <SrErrorMessage>{submitErrorMessage}</SrErrorMessage>}
          </Form>
        );
      }}
    />
  );
};

SrUtilityProfitAdminRegisterAddressForm.propTypes = {
  utilityPartnerId: PropTypes.number,
  utilityPartnerList: PropTypes.array,
  afterSubmit: PropTypes.func,
  dispatch: PropTypes.func.isRequired,
};

SrUtilityProfitAdminRegisterAddressForm.displayName = 'SrUtilityProfitAdminRegisterAddressForm';

export default SrUtilityProfitAdminRegisterAddressForm;
